import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Paper, Typography, FormControl, FormControlLabel, Switch, Checkbox, FormGroup, CircularProgress } from '@mui/material';
import axios from 'axios';
import ActionButton from '../../components/ActionButton';
import { showToast } from '../../utils/toastUtils';
import { Toaster } from 'sonner';

const NotificationSettings = () => {
  const [notifications, setNotifications] = useState({
    email: false,
    push: false,
    sms: false,
    newFeatures: false,
    updates: false,
    marketing: false,
  });
  const [loading, setLoading] = useState(true);

  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    } else {
      setLoading(false);
      showToast('Please log in again.', 'error');
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`/api/user/${userId}/details`);
      const userData = response.data.user;
      
      const notificationMethods = userData.notificationMethods ? userData.notificationMethods.split(',') : [];
      const notificationTypes = userData.notificationTypes ? userData.notificationTypes.split(',') : [];

      setNotifications({
        email: notificationMethods.includes('email'),
        push: notificationMethods.includes('push'),
        sms: notificationMethods.includes('sms'),
        newFeatures: notificationTypes.includes('newFeatures'),
        updates: notificationTypes.includes('updates'),
        marketing: notificationTypes.includes('marketing'),
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      showToast('Error fetching user details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    setNotifications({ ...notifications, [event.target.name]: event.target.checked });
  };

  const handleSave = async () => {
    if (!userId) {
      showToast('Please log in again.', 'error');
      return;
    }

    try {
      const payload = {
        userId,
        notificationMethods: {
          email: notifications.email,
          push: notifications.push,
          sms: notifications.sms
        },
        notificationTypes: {
          newFeatures: notifications.newFeatures,
          updates: notifications.updates,
          marketing: notifications.marketing
        }
      };

      const response = await axios.post('/api/user/update-notification-preferences', payload);
      showToast('Notification preferences saved successfully', 'success');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      showToast('Error saving notification preferences', 'error');
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Notification Settings</Typography>
      <FormControl component="fieldset" fullWidth margin="normal">
        <Typography variant="subtitle1" gutterBottom>Notification Methods</Typography>
        <FormGroup>
          <FormControlLabel
            control={<Switch checked={notifications.email} onChange={handleChange} name="email" />}
            label="Email Notifications"
          />
          <FormControlLabel
            control={<Switch checked={notifications.push} onChange={handleChange} name="push" />}
            label="Push Notifications"
          />
          <FormControlLabel
            control={<Switch checked={notifications.sms} onChange={handleChange} name="sms" />}
            label="SMS Notifications"
          />
        </FormGroup>
      </FormControl>
      <FormControl component="fieldset" fullWidth margin="normal">
        <Typography variant="subtitle1" gutterBottom>Notification Types</Typography>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={notifications.newFeatures} onChange={handleChange} name="newFeatures" />}
            label="New Features"
          />
          <FormControlLabel
            control={<Checkbox checked={notifications.updates} onChange={handleChange} name="updates" />}
            label="Product Updates"
          />
          <FormControlLabel
            control={<Checkbox checked={notifications.marketing} onChange={handleChange} name="marketing" />}
            label="Marketing Communications"
          />
        </FormGroup>
      </FormControl>
      <ActionButton
        text="Save"
        onClick={handleSave}
      />
      <Toaster position="top-right" className='mt-24'/>
    </Paper>
  );
};

export default NotificationSettings;
